import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Share2, Bookmark, User } from 'lucide-react';
import Navbar from '@/components/Navbar';

const blogContent = {
  'understanding-fractional-ownership': {
    title: 'Understanding Fractional Ownership: A Beginner\'s Guide',
    excerpt: 'Learn the fundamentals of fractional real estate investing and how it\'s democratizing access to premium properties for everyday investors.',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
    category: 'Education',
    author: 'ALTx Team',
    authorImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80',
    date: '2024-12-20',
    readTime: '5 min read',
    content: `
## What is Fractional Ownership?

Fractional ownership is a method of property purchase where multiple investors share the costs and benefits of owning a high-value asset. In the context of real estate, this means you can own a percentage of a premium property without having to purchase the entire property yourself.

### How Does It Work?

When you invest in fractional real estate through ALTx, here's what happens:

1. **Property Selection**: Our team identifies and vets premium properties with strong rental yields and appreciation potential.

2. **Legal Structure**: The property is held through a Special Purpose Vehicle (SPV) or Limited Liability Partnership (LLP), ensuring clear ownership rights for all investors.

3. **Investment**: You invest a minimum amount (starting from ₹10 Lakhs) and receive proportional ownership in the property.

4. **Returns**: You earn returns through:
   - **Rental Income**: Distributed monthly or quarterly based on your ownership percentage
   - **Capital Appreciation**: When the property is sold, you receive your share of any gains

### Benefits of Fractional Ownership

- **Lower Entry Barrier**: Access premium real estate with significantly lower capital
- **Diversification**: Spread your investment across multiple properties
- **Professional Management**: Properties are managed by experienced professionals
- **Liquidity**: Exit options available through secondary markets
- **Passive Income**: Regular rental distributions without landlord responsibilities

### Key Considerations

Before investing, consider:
- Investment tenure (typically 18-36 months)
- Your risk tolerance
- Liquidity needs
- Tax implications

Fractional ownership is revolutionizing how Indians invest in real estate, making premium properties accessible to a broader investor base.
    `
  },
  'indian-real-estate-2025': {
    title: 'Indian Real Estate Market Outlook for 2025',
    excerpt: 'Our analysts break down the key trends, emerging hotspots, and investment opportunities in India\'s evolving real estate landscape.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80',
    category: 'Market Insights',
    author: 'Research Desk',
    authorImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
    date: '2024-12-18',
    readTime: '8 min read',
    content: `
## Indian Real Estate: A 2025 Perspective

The Indian real estate market is poised for significant growth in 2025, driven by urbanization, infrastructure development, and changing work patterns.

### Key Market Trends

**1. Commercial Real Estate Revival**

The office sector is experiencing renewed demand as companies adopt hybrid work models:
- Grade A office spaces in top metros seeing increased absorption
- Flex spaces growing at 25% annually
- IT/ITES sector driving demand in Bangalore, Hyderabad, and Pune

**2. Residential Market Dynamics**

- Premium and luxury segments outperforming affordable housing
- NRI investments increasing due to favorable exchange rates
- Integrated townships gaining popularity

**3. Emerging Investment Hotspots**

- **Mumbai**: BKC, Lower Parel, and Navi Mumbai
- **Bangalore**: North Bangalore and Electronic City
- **Hyderabad**: Financial District and Gachibowli
- **Chennai**: OMR corridor and suburban areas

### Infrastructure Catalysts

Major infrastructure projects boosting real estate values:
- Mumbai Trans-Harbour Link
- Bangalore Metro expansion
- Delhi-Mumbai Industrial Corridor
- Smart City initiatives across 100 cities

### Investment Outlook

Expected returns for 2025:
- Commercial: 8-12% rental yield + 5-8% appreciation
- Residential Premium: 6-10% rental yield + 8-15% appreciation
- Warehousing: 9-11% rental yield

The combination of favorable demographics, infrastructure spending, and economic growth makes Indian real estate an attractive proposition for investors seeking stable returns.
    `
  },
  'commercial-vs-residential': {
    title: 'Commercial vs Residential: Which Investment is Right for You?',
    excerpt: 'A comprehensive comparison of commercial and residential fractional investments, including risk profiles, returns, and liquidity considerations.',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
    category: 'Investment Tips',
    author: 'Investment Team',
    authorImage: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&q=80',
    date: '2024-12-15',
    readTime: '6 min read',
    content: `
## Choosing Between Commercial and Residential Investments

Both commercial and residential properties offer unique advantages for fractional investors. Understanding their differences is crucial for building a balanced portfolio.

### Commercial Real Estate

**Pros:**
- Higher rental yields (8-12% vs 2-4% for residential)
- Longer lease terms (3-9 years)
- Professional tenants (corporates)
- Triple net leases reduce owner expenses

**Cons:**
- Higher entry costs
- More sensitive to economic cycles
- Longer vacancy periods when empty

### Residential Real Estate

**Pros:**
- More familiar asset class
- Broader tenant pool
- Shorter vacancy periods
- Better capital appreciation potential

**Cons:**
- Lower rental yields
- More management intensive
- Higher tenant turnover

### Making Your Decision

Consider these factors:

| Factor | Commercial | Residential |
|--------|------------|-------------|
| Min. Investment | ₹25L+ | ₹10L+ |
| Typical Yield | 8-12% | 2-4% |
| Tenure | 3-5 years | 2-3 years |
| Risk Level | Medium-High | Low-Medium |

**Our Recommendation**: For most investors, a mix of both asset types provides optimal diversification. Start with residential for stability, then add commercial for higher yields.
    `
  },
  'art-investment-introduction': {
    title: 'Art as an Asset Class: Why Smart Investors are Diversifying',
    excerpt: 'Discover how blue-chip artwork is becoming an increasingly popular alternative investment, offering both aesthetic pleasure and financial returns.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1200&q=80',
    category: 'Alternative Assets',
    author: 'ALTx Team',
    authorImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80',
    date: '2024-12-12',
    readTime: '7 min read',
    content: `
## The Rise of Art Investment

Art has long been the preserve of ultra-high-net-worth individuals. Through fractional ownership, this asset class is now accessible to a broader range of investors.

### Why Invest in Art?

**1. Portfolio Diversification**
Art has low correlation with traditional asset classes like stocks and bonds, making it an excellent diversification tool.

**2. Inflation Hedge**
Historically, blue-chip art has maintained or increased its value during inflationary periods.

**3. Strong Historical Returns**
The contemporary art market has delivered average annual returns of 7.5-14% over the past two decades.

### ALTx Partnership with Masha Art

We've partnered with Masha Art, a leading art advisory firm, to bring curated art investment opportunities to our platform:

- Access to works by established Indian and international artists
- Expert curation and authentication
- Secure storage and insurance
- Regular valuations and reporting

### What to Expect

- **Minimum Investment**: ₹25 Lakhs
- **Typical Tenure**: 12-36 months
- **Expected Returns**: 12-35% annually
- **Exit**: Through auction houses or private sales

Art investment combines the pleasure of owning beautiful works with the potential for significant financial returns.
    `
  },
  'tax-benefits-fractional': {
    title: 'Tax Benefits of Fractional Real Estate Investment in India',
    excerpt: 'Understanding the tax implications and benefits of investing in fractional real estate, including capital gains and rental income treatment.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80',
    category: 'Finance',
    author: 'Finance Team',
    authorImage: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&q=80',
    date: '2024-12-10',
    readTime: '6 min read',
    content: `
## Understanding Tax Implications

Tax planning is crucial for maximizing returns from fractional real estate investments. Here's what you need to know.

### Rental Income Taxation

Rental income from fractional ownership is taxed as "Income from House Property":

- **Standard Deduction**: 30% of net rental income
- **Interest Deduction**: On loans taken for investment
- **Tax Rate**: As per your income tax slab

### Capital Gains Tax

When the property is sold:

**Short-term (< 24 months)**
- Taxed as per your income slab
- No indexation benefit

**Long-term (> 24 months)**
- 20% with indexation
- 12.5% without indexation (from 2024)

### Tax-Efficient Strategies

1. **Hold for Long Term**: Benefit from lower LTCG rates
2. **Reinvest Under Section 54F**: Defer capital gains tax
3. **Consider SPV Structure**: May offer additional benefits

### Important Considerations

- Maintain proper documentation
- Report all rental income
- Track cost of acquisition with indexation
- Consult a tax professional for personalized advice

Understanding these tax implications helps you plan better and maximize your post-tax returns.
    `
  },
  'due-diligence-checklist': {
    title: 'The Ultimate Due Diligence Checklist for Property Investments',
    excerpt: 'A step-by-step guide to evaluating fractional real estate opportunities, from legal verification to market analysis.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
    category: 'Education',
    author: 'Legal Team',
    authorImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
    date: '2024-12-08',
    readTime: '10 min read',
    content: `
## Due Diligence: Your Investment Protection

Before investing in any fractional property, thorough due diligence is essential. Here's our comprehensive checklist.

### Legal Verification

- [ ] Clear title verification
- [ ] Encumbrance certificate review
- [ ] Approved building plans
- [ ] Occupancy certificate
- [ ] SPV/LLP documentation
- [ ] Shareholder agreement terms

### Financial Analysis

- [ ] Property valuation report
- [ ] Rental yield projections
- [ ] Operating expense breakdown
- [ ] Exit strategy and timeline
- [ ] Historical price trends in area

### Physical Inspection

- [ ] Property condition assessment
- [ ] Infrastructure quality
- [ ] Amenities and facilities
- [ ] Neighborhood analysis
- [ ] Future development plans nearby

### Tenant Analysis (For Leased Properties)

- [ ] Tenant creditworthiness
- [ ] Lease terms and conditions
- [ ] Rental escalation clauses
- [ ] Lock-in period
- [ ] Security deposit adequacy

### Platform Due Diligence

- [ ] Platform's track record
- [ ] Previous project performance
- [ ] Management team credentials
- [ ] Regulatory compliance
- [ ] Investor grievance mechanism

### Documentation to Collect

1. Property title documents
2. Sale deed/lease agreement
3. Tax payment receipts
4. NOCs from authorities
5. Building approval plans
6. SPV incorporation documents

At ALTx, we conduct thorough due diligence on every property, but informed investors make the best partners.
    `
  }
};

const relatedPosts = [
  {
    id: 'understanding-fractional-ownership',
    title: 'Understanding Fractional Ownership',
    category: 'Education'
  },
  {
    id: 'indian-real-estate-2025',
    title: 'Indian Real Estate Market Outlook',
    category: 'Market Insights'
  },
  {
    id: 'tax-benefits-fractional',
    title: 'Tax Benefits of Fractional Investment',
    category: 'Finance'
  }
];

export default function BlogDetail({ user, setUser }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const post = blogContent[slug];
  
  if (!post) {
    return (
      <div className="min-h-screen bg-[#030303] text-white">
        <Navbar user={user} setUser={setUser} />
        <div className="pt-32 text-center">
          <h1 className="text-2xl mb-4">Article not found</h1>
          <button
            onClick={() => navigate('/blog')}
            className="text-[#4ADE80] hover:underline"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Navbar user={user} setUser={setUser} />
      
      <article className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative h-[400px] md:h-[500px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/60 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <button
                onClick={() => navigate('/blog')}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </button>
              
              <span className="inline-block px-3 py-1 bg-[#4ADE80]/20 text-[#4ADE80] text-sm rounded-full border border-[#4ADE80]/30 mb-4">
                {post.category}
              </span>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-6">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <img
                    src={post.authorImage}
                    alt={post.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.date)}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex gap-4 py-6 border-b border-white/10 mb-8"
          >
            <button className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
              <Bookmark className="w-4 h-4" />
              Save
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-light prose-headings:text-white
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-white/70 prose-p:leading-relaxed
              prose-li:text-white/70
              prose-strong:text-white prose-strong:font-medium
              prose-a:text-[#4ADE80] prose-a:no-underline hover:prose-a:underline
              prose-table:border-white/10
              prose-th:text-white prose-th:font-medium prose-th:bg-white/5
              prose-td:text-white/70 prose-td:border-white/10
            "
            dangerouslySetInnerHTML={{ 
              __html: post.content
                .replace(/^## /gm, '<h2>')
                .replace(/^### /gm, '<h3>')
                .replace(/\n\n/g, '</p><p>')
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                .replace(/- \[[ x]\]/g, '•')
                .replace(/^\| /gm, '<tr><td>')
                .replace(/ \| /g, '</td><td>')
                .replace(/ \|$/gm, '</td></tr>')
            }}
          />

          {/* Related Articles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <h3 className="text-xl font-light mb-6">Related Articles</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {relatedPosts.filter(p => p.id !== slug).slice(0, 3).map((relatedPost) => (
                <button
                  key={relatedPost.id}
                  onClick={() => navigate(`/blog/${relatedPost.id}`)}
                  className="text-left p-4 bg-[#0A0A0A] border border-white/5 rounded-lg hover:border-white/20 transition-all"
                >
                  <span className="text-xs text-[#4ADE80] mb-2 block">{relatedPost.category}</span>
                  <span className="text-sm text-white/80">{relatedPost.title}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </article>
    </div>
  );
}
